import { useEffect, useRef, useState } from 'react';

/**
 * One-shot reveal-on-scroll hook.
 * Sets `visible` to true when the element first enters the viewport,
 * then disconnects. Safe for SSR / environments without IntersectionObserver.
 */
export default function useReveal(options) {
  const ref = useRef(null);
  const observerRef = useRef(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;

    if (typeof IntersectionObserver === 'undefined') {
      setVisible(true);
      return undefined;
    }

    const obs = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) {
          setVisible(true);
          obs.disconnect();
          observerRef.current = null;
        }
      },
      { threshold: 0.15, ...(options || {}) }
    );
    observerRef.current = obs;
    obs.observe(el);

    return () => {
      obs.disconnect();
      observerRef.current = null;
    };
    // Intentionally run-once: ref + options snapshot at mount.
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return [ref, visible];
}
